// Public data-deletion instructions. URL: https://jarvis.yourproject.ai/data-deletion
// This is the URL pasted into the Facebook App settings "User Data Deletion
// Instructions URL" field. Meta's reviewers click through this so the copy
// needs to read as a real, actionable, plain-language guide.

import { LegalLayout, Section } from './LegalLayout'
import Seo from '@/components/Seo'

export function DataDeletion() {
  return (
    <>
    <Seo title="Data Deletion — Jarvis" description="How to request deletion of your Jarvis data." path="/data-deletion" />
    <LegalLayout
      title="Data Deletion Instructions"
      subtitle="How to remove your data from Jarvis at any time."
      effectiveDate="May 12, 2026"
    >
      <p>
        Jarvis (operated by <strong>ClearFinOps LLC</strong>) takes data deletion seriously. You
        can remove your data from our Service yourself in two ways, depending on what you want
        to delete:
      </p>

      <Section heading="Option 1 — Disconnect a single integration">
        <p>
          If you only want Jarvis to stop using a single third-party integration (for example,
          you want to revoke our access to your Facebook ad accounts but keep your Jarvis
          account):
        </p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>Sign in at <a className="text-jarvis-blue hover:underline" href="https://jarvis.yourproject.ai/login">jarvis.yourproject.ai</a>.</li>
          <li>Open <strong>Settings</strong> from the left navigation.</li>
          <li>Scroll to the integration you want to remove (for example, &ldquo;Facebook Ads&rdquo;).</li>
          <li>Click <strong>Disconnect</strong>.</li>
        </ol>
        <p>
          When you click Disconnect, we immediately delete the encrypted access token for that
          integration so we can no longer call its API. Any historical analytical snapshots
          derived from that integration are retained for up to ninety (90) days and then purged
          automatically.
        </p>
      </Section>

      <Section heading="Option 2 — Delete your entire Jarvis account">
        <p>
          If you want to delete everything we hold about you:
        </p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>
            Email{' '}
            <a className="text-jarvis-blue hover:underline" href="mailto:kc@yourproject.ai?subject=Delete%20my%20Jarvis%20account">
              kc@yourproject.ai
            </a>{' '}
            from the address associated with your Jarvis account.
          </li>
          <li>Use the subject line &ldquo;Delete my Jarvis account.&rdquo;</li>
          <li>We will confirm receipt within one (1) business day.</li>
          <li>
            Your account, connected third-party tokens, and all personal data are permanently
            deleted within thirty (30) days of the confirmation.
          </li>
        </ol>
        <p>
          We may retain a minimal record of the deletion request (your email address and the
          deletion date) for compliance, audit, and abuse-prevention purposes, as well as any
          records we are required by law to retain (for example, tax records related to past
          subscription payments).
        </p>
      </Section>

      <Section heading="Revoking access from Facebook">
        <p>
          If you want to revoke Jarvis&rsquo;s access to your Facebook account directly from
          Facebook&rsquo;s side (instead of from within Jarvis):
        </p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>
            Open{' '}
            <a className="text-jarvis-blue hover:underline" target="_blank" rel="noreferrer" href="https://www.facebook.com/settings?tab=business_tools">
              Facebook Settings → Business Integrations
            </a>.
          </li>
          <li>Find the &ldquo;Jarvis&rdquo; (or yourproject.ai) entry in the list.</li>
          <li>Click <strong>Remove</strong> and confirm.</li>
        </ol>
        <p>
          Once you remove access on Facebook, any subsequent attempt by Jarvis to call the
          Facebook Marketing API on your behalf will fail and the integration will be marked
          disconnected in your Jarvis Settings page.
        </p>
      </Section>

      <Section heading="What gets deleted">
        <p>
          When you delete your Jarvis account we permanently remove:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Your user profile (name, email, company information).</li>
          <li>
            All third-party integration credentials, including any Facebook, Stripe, and Google
            access tokens.
          </li>
          <li>
            All advertising-performance snapshots, briefings, tasks, and AI-generated analyses
            tied to your account.
          </li>
          <li>Any phone-call transcripts and voice agent recordings associated with your account.</li>
        </ul>
        <p>
          Limited records we are required to retain by law (for example, payment receipts for
          tax purposes) are kept for the period required and then deleted.
        </p>
      </Section>

      <Section heading="Questions">
        <p>
          If you need help with deletion, or if you have questions about what data we hold,
          email{' '}
          <a className="text-jarvis-blue hover:underline" href="mailto:kc@yourproject.ai">kc@yourproject.ai</a>.
          You can also write to ClearFinOps LLC, Lehi, Utah, USA.
        </p>
      </Section>
    </LegalLayout>
    </>
  )
}
